import { Page, pathIsPage } from "@src/shared/pages";
import React, { useCallback, useContext } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import { DownIcon, UpIcon } from "@src/components/elements";
import { navContext } from "@src/contexts/NavContext";
import { CurrentUser } from "@src/components/modules/Header/CurrentUser";
import { UniversalSearchModal } from "@src/components/modules/Nav/UniversalSearchModal";

const StyledNav = styled.nav`
  display: flex;
  flex-direction: column;
  box-shadow: var(--drop-shadow-dark);
  overflow: auto;

  ul {
    display: flex;
    flex-direction: column;
    list-style: none;
    margin: 0;
    padding: 0.5rem;
    gap: 0.125rem;
  }

  svg {
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;
    fill: var(--highlight-green);
    transition: var(--transition);
    g path {
      fill: var(--highlight-green);
    }
  }

  li:has(a.active) {
    background-color: var(--dark-gray);
    border-radius: var(--rounded-corner-large);
  }

  .sub-menu {
    .nav-link {
      padding-left: 2.75rem;
    }
  }

  .nav-link {
    text-decoration: none;
    display: flex;
    gap: 0.5rem;
    justify-content: start;
    align-items: center;
    cursor: pointer;
    font-size: var(--font-size-xsmall);
    font-weight: var(--font-weight-bold);
    color: var(--border-gray);
    padding: 0.5rem 0.75rem;
    outline: 2px solid transparent;
    outline-offset: -2px;

    &:first-of-type {
      border-top-left-radius: var(--rounded-corner-large);
      border-bottom-left-radius: var(--rounded-corner-large);
    }
    &:last-of-type {
      border-top-right-radius: var(--rounded-corner-large);
      border-bottom-right-radius: var(--rounded-corner-large);
    }

    &:has(~ .shortcut-button:hover) {
      outline: 2px solid var(--dark-gray);
    }

    &.shortcut-button {
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        fill: var(--highlight-green);
      }
      &:hover {
        background-color: var(--dark-gray);
        color: var(--highlight-green);
      }
    }

    &:hover {
      background-color: var(--dark-gray);
      color: var(--highlight-green);
      & ~ .shortcut-button {
        outline: 2px solid var(--dark-gray);
      }
      svg {
        fill: var(--highlight-green);
      }
    }
    &.active {
      text-decoration: none;
      color: var(--primary-black);
      background-color: var(--highlight-green);
      &:has(~ .shortcut-button:hover) {
        outline: 2px solid transparent;
      }
      & ~ .shortcut-button {
        background-color: var(--secondary-black);
        outline: 2px solid var(--highlight-green);
        &:hover {
          background-color: var(--highlight-green);
          svg {
            fill: var(--primary-black);
          }
        }
        svg {
          fill: var(--highlight-green);
        }
      }
      svg {
        fill: var(--primary-black);
        g path {
          fill: var(--primary-black);
        }
      }
    }

    span {
      flex-grow: 1;
    }
  }
  .expandable-link {
    border-radius: var(--rounded-corner-large);
  }
`;

interface PageProps {
  activePage?: Page;
}

export const Nav: React.FC<PageProps> = ({}) => {
  const { navItems, onMenuToggle, path, showSearch, setShowSearch } =
    useContext(navContext);

  const getNavClassName = useCallback(
    (currPages: Page[]): string | undefined => {
      if (currPages.some((page) => pathIsPage(path, page))) {
        return "active nav-link";
      }
      return "nav-link";
    },
    [path],
  );

  return (
    <StyledNav>
      {showSearch && (
        <UniversalSearchModal onClose={() => setShowSearch(false)} />
      )}

      <CurrentUser />
      <ul>
        {navItems.map((navItem, idx) => {
          if (navItem.canShow === false) return undefined;
          if (navItem.type === "link") {
            return (
              <li key={`${navItem.to} ${idx}`}>
                <div className="row no-gap">
                  <Link
                    className={getNavClassName(navItem.activePages)}
                    to={navItem.to}
                    style={{ flexGrow: 1 }}
                  >
                    {navItem.display}
                  </Link>
                  {navItem.sibling ? (
                    <Link
                      className={`${getNavClassName(
                        navItem.activePages,
                      )} shortcut-button`}
                      to={navItem.sibling.to}
                    >
                      {navItem.sibling.display}
                    </Link>
                  ) : null}
                </div>
              </li>
            );
          }

          if (navItem.type === "function") {
            return (
              <li key={`${navItem.to} ${idx}`}>
                <Link
                  to="#"
                  className={getNavClassName(navItem.activePages)}
                  onClick={(e) => {
                    e.preventDefault();
                    navItem.to();
                  }}
                  style={{ flexGrow: 1 }}
                >
                  {navItem.display}
                </Link>
              </li>
            );
          }

          if (
            navItem.hideIfEmpty === true &&
            !navItem.links.some((l) => !!l.canShow)
          ) {
            return null;
          }

          return (
            <li key={`${navItem.type} ${idx}`}>
              <div
                className="nav-link expandable-link"
                role="button"
                onClick={() => {
                  onMenuToggle(idx);
                }}
              >
                {navItem.display} {navItem.isOpen ? <UpIcon /> : <DownIcon />}
              </div>
              {navItem.isOpen && (
                <div className="sub-menu">
                  {navItem.links.map((link, linkIdx) =>
                    link.canShow !== false ? (
                      <div key={`${link.to} ${linkIdx}`}>
                        {link.type === "function" ? (
                          <Link
                            to="#"
                            className={getNavClassName(link.activePages)}
                            role="button"
                            onClick={link.to}
                          >
                            {link.display}
                          </Link>
                        ) : (
                          <Link
                            className={getNavClassName(link.activePages)}
                            to={link.to}
                          >
                            {link.display}
                          </Link>
                        )}
                      </div>
                    ) : null,
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </StyledNav>
  );
};
